// I suspect this will be temporary until we can move to the database. But failing that, it's a good model.
import { DEFAULT_CATEGORY_SORT_SLUG } from '../../lib/config/categoryDefaults'

export type FilterType =
  | 'exclusive'
  | 'inStock'
  | 'discounted'
  | 'apps'
  | 'books'
  | 'downloads'
  | 'ebooks'
  | 'dvds'
  | 'tricks'

export type CategoryBrowseGroupId =
  | 'playing-cards'
  | 'performance-style'
  | 'books-media'
  | 'digital'
  | 'coins-money'
  | 'wallets'
  | 'classic-props'
  | 'beginners'
  | 'apps-brand-starter'

export type CategoryDirectoryConfig = {
  groupId: CategoryBrowseGroupId
  order: number
  blurb?: string
  label?: string
  href?: string
}

export type CategoryDirectoryLink = {
  slug: string
  href: string
  label: string
  blurb?: string
}

export type CategoryDirectoryGroup = {
  id: CategoryBrowseGroupId
  title: string
  description: string
  links: CategoryDirectoryLink[]
}

export interface CategoryConfig {
  showFilter?: boolean
  productListStyle?: 'standard' | 'grid'
  thumbnailStyle?: 'default' | 'square' | 'video'
  filters?: FilterType[]
  breadcrumbs?: Array<{
    name: string
    href?: string
  }>
  showProductTypeInFilter?: boolean
  showStockInFilter?: boolean
  productTypeInFilter?: string
  /** Name of footer component in app/category/Footers/ (e.g. MagicApps) */
  footerName?: string
  /** HTML block shown below the listing (page 1) for on-page SEO */
  seoText?: string
  /** HTML block shown below the H1, overriding the database intro when set. */
  introHtml?: string
  /** Overrides meta description in variant category generateMetadata when set */
  metaDescription?: string
  /** Overrides title segment in generateMetadata (page 1 only); still gets " | Vanishing Inc. Magic" suffix */
  metaTitle?: string
  /** Optional promo module to render in right sidebar beneath filters */
  sidebarModal?: 'tenByTenDownloadsSignup' | 'magicInMindSignup'
  /** When true, sidebar shows a "Top sellers" block (GraphQL salesRank, same filters as listing) */
  showTopSellersSidebar?: boolean
  /** Full H2 for the top sellers block; when unset, uses "Top Selling {category display name}" */
  topSellersHeading?: string
  /**
   * Default sort URL segment for this category’s index and omitted sort (see translateSort keys).
   * When unset, uses global DEFAULT_CATEGORY_SORT_SLUG (newest).
   */
  defaultSortSlug?: string
  /**
   * Permanent redirect target for retired category roots.
   * Product URLs below the old category still resolve before this redirect runs.
   */
  redirectTo?: string
  /**
   * Set false for config-only aliases that should not be treated as category URL roots.
   */
  includeInCategoryRouting?: boolean
  // Routing configuration
  routing?: {
    // If true, bypass middleware rewriting (has custom page in app/)
    bypassMiddleware?: boolean
    // If true, use custom brand variant component (in app/category/[slug]/brandVariant*.tsx)
    useCustomComponent?: boolean
    // Article route pattern override. Default is 'article' (routes to /category/article/slug)
    // Set to empty string or null for direct routing (e.g., /category/slug for playing-cards)
    // Set to custom string for custom pattern (e.g., 'blog' for /category/blog/slug)
    articleRoutePattern?: string | null
  }
  /** Enables this category on the /category/ browse page. */
  directory?: CategoryDirectoryConfig
}

const DEFAULT_CATEGORY_CONFIG: CategoryConfig = {
  showFilter: true,
  productListStyle: 'standard',
  showProductTypeInFilter: true,
  showStockInFilter: true,
  productTypeInFilter: 'products',
  filters: [
    'exclusive',
    'inStock',
    'discounted',
    'books',
    'downloads',
    'dvds',
    'tricks',
  ],
}

export const categoryConfigs = {
  'magic-magazines': {
    directory: {
      groupId: 'books-media',
      order: 3,
      label: 'Magic magazines',
      blurb: 'Magic periodicals, interviews, and collectible magazine issues.',
    },
    showFilter: true,
    productListStyle: 'standard',
    productTypeInFilter: 'magazines',
    showProductTypeInFilter: false,
    showStockInFilter: true,
  },
  'card-magic-downloads': {
    directory: {
      groupId: 'digital',
      order: 3,
      label: 'Card magic downloads',
      blurb:
        'Downloadable card magic teaching — sleights, routines, and full acts.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    showStockInFilter: false,
    productTypeInFilter: 'downloads',
    filters: ['exclusive', 'discounted'],
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads' },
      { name: 'Card magic downloads', href: '#' },
    ],
    sidebarModal: 'tenByTenDownloadsSignup',
    showTopSellersSidebar: true,
  },
  ebooks: {
    directory: {
      groupId: 'digital',
      order: 1,
      label: 'Ebooks',
      blurb: 'Thousands of magic ebooks from classics to the latest releases.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    showStockInFilter: false,
    productTypeInFilter: 'downloads',
    filters: ['exclusive', 'discounted'],
    sidebarModal: 'magicInMindSignup',
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads' },
      { name: 'Ebooks', href: '#' },
    ],
    showTopSellersSidebar: true,
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <h2 class="mt-0 text-2xl font-bold text-gray-900">Get the most out of our magic ebooks!</h2>
        <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
          <li><a href="/ebooks/articles/magic-ebooks-formatting/" class="text-vi-red hover:underline">Vanishing Inc. Magic Ebooks Formatting</a></li>
          <li><a href="/ebooks/articles/mastering-magic-on-the-go/" class="text-vi-red hover:underline">Mastering Magic on the Go</a></li>
          <li><a href="/ebooks/articles/the-power-of-magic-ebooks/" class="text-vi-red hover:underline">The Power of magic Ebooks</a></li>
        </ul>
      </div>
    `,
  },
  'playing-cards': {
    directory: {
      groupId: 'playing-cards',
      order: 1,
      blurb:
        'Playing cards, rare drops, and curated decks for workers and collectors.',
    },
    showFilter: false,
    productListStyle: 'grid',
    thumbnailStyle: 'square',
    routing: {
      bypassMiddleware: true, // Has custom route at app/playing-cards/[...slugList]/page.tsx
    },
  },
  'playing-card-storage': {
    directory: {
      groupId: 'playing-cards',
      order: 2,
      label: 'Playing card storage',
      blurb: 'Cases, clips, and organizers to protect and carry your decks.',
    },
    showFilter: false,
    productListStyle: 'grid',
    thumbnailStyle: 'video',
    breadcrumbs: [
      { name: 'Playing Cards', href: '/playing-cards' },
      { name: 'Card Storage', href: '#' },
    ],
  },
  'cardistry-decks': {
    directory: {
      groupId: 'playing-cards',
      order: 3,
      label: 'Cardistry decks',
      blurb:
        'Decks built for cuts, fans, and cardistry — design meets handling.',
    },
    showFilter: false,
    productListStyle: 'grid',
    thumbnailStyle: 'square',
    breadcrumbs: [
      { name: 'Playing Cards', href: '/playing-cards' },
      { name: 'Cardistry decks', href: '#' },
    ],
  },
  'card-clips': {
    directory: {
      groupId: 'playing-cards',
      order: 4,
      label: 'Card clips',
      blurb: 'Clips and carriers that keep a deck ready in your pocket or bag.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'rare-magic-ebooks': {
    directory: {
      groupId: 'digital',
      order: 2,
      label: 'Rare magic ebooks',
      blurb: 'Hard-to-find and collectible magic ebooks in digital form.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    showStockInFilter: false,
    productTypeInFilter: 'downloads',
    filters: ['exclusive', 'discounted'],
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads' },
      { name: 'Magic ebooks', href: '/ebooks' },
      { name: 'Rare magic ebooks', href: '#' },
    ],
  },
  'magic-books': {
    directory: {
      groupId: 'books-media',
      order: 1,
      label: 'Magic books',
      blurb:
        'The best way to learn deeply — theory, technique, and full routines in print.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    productTypeInFilter: 'books',
    filters: ['exclusive', 'inStock'],
    showStockInFilter: true,
    sidebarModal: 'magicInMindSignup',
    showTopSellersSidebar: true,
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <h2 class="mt-0 text-2xl font-bold text-gray-900">Magic Books</h2>
        <p class="leading-relaxed text-gray-700">
          We really, really love magic books here at Vanishing Inc. We publish more than anyone else and pride ourselves on the quality of the magic books we make. We honestly believe magic books are the best way to <a href="/learn-magic-tricks" class="text-vi-red hover:underline">learn magic tricks</a>, and urge you try if this way to learn <a href="/magic-tricks" class="text-vi-red hover:underline">magic tricks</a> is new to you. When <a href="/sleight-of-hand" class="text-vi-red hover:underline">sleight of hand</a> is explained in a book you will learn in a more efficient way. If you're interested in learning <a href="/mentalism" class="text-vi-red hover:underline">mentalism</a>, <a href="/close-up-magic" class="text-vi-red hover:underline">close-up magic</a>, <a href="/stage-and-parlor-magic" class="text-vi-red hover:underline">stage and parlor magic</a>, <a href="/money-magic" class="text-vi-red hover:underline">coin tricks</a> or <a href="/virtual-magic-shows" class="text-vi-red hover:underline">magic for Zoom</a>, you'll find a book here to help you on your journey. If you want to <a href="/learn-card-tricks" class="text-vi-red hover:underline">learn card tricks</a> with <a href="/playing-cards" class="text-vi-red hover:underline">playing cards</a>, you'll find more books here than you could possibly get through in a lifetime!
        </p>
        <p class="mt-4 leading-relaxed text-gray-700">
          Here are some articles about magic books we hope you'll enjoy reading.
        </p>
        <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
          <li><a href="/magic-books/articles/recommended-magic-books-for-beginners/" class="text-vi-red hover:underline">Recommended Magic Books For Beginners</a></li>
          <li><a href="/magic-books/articles/what-is-a-magic-book-test/" class="text-vi-red hover:underline">What Is A Magic Book Test?</a></li>
          <li><a href="/magic-books/articles/the-best-selling-magic-books-of-all-time/" class="text-vi-red hover:underline">The Best-Selling Magic Books Of All Time</a></li>
          <li><a href="/magic-books/articles/magic-coloring-book/" class="text-vi-red hover:underline">The Magic Coloring Book</a></li>
          <li><a href="/magic-books/articles/best-magic-books-for-beginners/" class="text-vi-red hover:underline">The Five Best Magic Books For Beginners</a></li>
          <li><a href="/magic-books/articles/when-is-a-magic-book-not-a-magic-book/" class="text-vi-red hover:underline">When is a Magic Book Not a Magic Book?</a></li>
          <li><a href="/magic-books/articles/how-to-write-and-publish-a-magic-book/" class="text-vi-red hover:underline">How to Write and Publish a Magic Book</a></li>
          <li><a href="/magic-books/articles/best-books-for-intermediate-to-advanced-magic/" class="text-vi-red hover:underline">Best Books for Intermediate to Advanced Magic</a></li>
          <li><a href="/magic-books/articles/the-best-magic-theory-books/" class="text-vi-red hover:underline">The Best Magic Theory Books</a></li>
          <li><a href="/magic-books/articles/best-selling-magic-books/" class="text-vi-red hover:underline">Best Selling Magic Books of 2024</a></li>
        </ul>
      </div>
    `,
  },
  'magic-dvds': {
    directory: {
      groupId: 'books-media',
      order: 2,
      label: 'Magic DVDs',
      blurb:
        'Classic and modern magic on disc — performances and teaching you can rewatch.',
    },
    showProductTypeInFilter: false,
  },
  'close-up-magic-downloads': {
    directory: {
      groupId: 'digital',
      order: 4,
      label: 'Close-up magic downloads',
      blurb:
        'Video instruction for restaurant, walk-around, and tabled close-up.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    productTypeInFilter: 'downloads',
    filters: ['exclusive', 'discounted'],
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads' },
      { name: 'Close-up magic downloads', href: '#' },
    ],
  },
  'coin-magic-downloads': {
    directory: {
      groupId: 'digital',
      order: 5,
      label: 'Coin magic downloads',
      blurb: 'Learn coin sleights and routines from top teachers, on demand.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    productTypeInFilter: 'downloads',
    filters: ['exclusive', 'discounted'],
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads' },
      { name: 'Coin magic downloads', href: '#' },
    ],
    showTopSellersSidebar: true,
  },
  'mentalism-downloads': {
    directory: {
      groupId: 'digital',
      order: 6,
      label: 'Mentalism downloads',
      blurb: 'Mind-reading, predictions, and psychological magic as downloads.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
    productListStyle: 'standard',
    productTypeInFilter: 'downloads',
    filters: ['exclusive', 'discounted'],
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads' },
      { name: 'Mentalism downloads', href: '#' },
    ],
  },
  'money-magic': {
    directory: {
      groupId: 'performance-style',
      order: 4,
      blurb:
        'Tricks with bills and coins — strong hooks and real-world angles.',
    },
    showFilter: true,
    showProductTypeInFilter: true,
    showStockInFilter: true,
    productListStyle: 'standard',
    showTopSellersSidebar: true,
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <div>
            <h2 class="mt-0 text-xl font-bold text-gray-900">Money Magic</h2>
            <p class="leading-relaxed text-gray-700">
              Money magic is the term for <a href="/magic-tricks" class="text-vi-red hover:underline">magic tricks</a> using, well, money. Could be regular coins, <a href="/coin-gimmicks/" class="text-vi-red hover:underline">gaffed coins</a> or bills. Dai Vernon famously taught that good magic always has an emotional hook. And, you cannot get much stronger an emotional hook than money. You can get money magic that is good for <a href="/close-up-magic" class="text-vi-red hover:underline">close-up magic</a>, <a href="/stage-and-parlor-magic" class="text-vi-red hover:underline">stage or parlor magic</a>, <a href="/virtual-magic-shows" class="text-vi-red hover:underline">virtual magic shows</a> and even <a href="/mentalism" class="text-vi-red hover:underline">mentalism</a>. Making money appear or disappear using <a href="/flash-paper" class="text-vi-red hover:underline">flash paper</a> is always popular. If you perform mainly with <a href="/playing-cards" class="text-vi-red hover:underline">playing cards</a> then some coin tricks make a nice change. Coin magic is always a good start when you want to <a href="/learn-magic-tricks" class="text-vi-red hover:underline">learn magic tricks</a> and <a href="/sleight-of-hand" class="text-vi-red hover:underline">sleight of hand</a>.
            </p>
          </div>
          <div>
            <h2 class="mt-0 text-xl font-bold text-gray-900">Money Magic Articles</h2>
            <p class="leading-relaxed text-gray-700">Here are some articles about money magic we hope you find interesting:</p>
            <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
              <li><a href="/money-magic/articles/advanced-coin-magic/" class="text-vi-red hover:underline">Where can you go to learn intermediate to advanced coin tricks?</a></li>
              <li><a href="/money-magic/articles/easy-magic-tricks-with-coins/" class="text-vi-red hover:underline">Easy Magic Tricks With Coins</a></li>
              <li><a href="/money-magic/articles/most-popular-money-magic-tricks/" class="text-vi-red hover:underline">Our Most Popular Money Magic Tricks</a></li>
              <li><a href="/money-magic/articles/how-to-do-magic-tricks-with-coins/" class="text-vi-red hover:underline">How To Do Magic Tricks With Coins</a></li>
              <li><a href="/money-magic/articles/learn-magic-with-money/" class="text-vi-red hover:underline">Learn Magic With Money</a></li>
              <li><a href="/money-magic/articles/glossary-of-terms-used-in-coin-magic/" class="text-vi-red hover:underline">Glossary of Terms Used In Coin Magic</a></li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  'stage-and-parlor-magic': {
    directory: {
      groupId: 'performance-style',
      order: 5,
      label: 'Stage & parlor magic',
      blurb: 'Material scaled for platform, cabaret, and larger audiences.',
    },
    showTopSellersSidebar: true,
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <div>
            <h2 class="mt-0 text-2xl font-bold text-gray-900">Stage and Parlor Magic</h2>
            <p class="leading-relaxed text-gray-700">
              Today, more and more people that perform <a href="/close-up-magic" class="text-vi-red hover:underline">close up magic</a> want to get into performing larger scale magic. That could be <a href="/mentalism" class="text-vi-red hover:underline">mentalism</a>, <a href="/card-magic" class="text-vi-red hover:underline">card magic tricks</a>, <a href="/money-magic" class="text-vi-red hover:underline">money magic</a>, or classics like the <a href="/rings/" class="text-vi-red hover:underline">linking rings</a>. After people got a taste for <a href="/virtual-magic-shows" class="text-vi-red hover:underline">virtual magic shows</a>, now is the perfect time to take the step to performing <a href="/magic-tricks" class="text-vi-red hover:underline">magic tricks</a> on a platform or stage. We have got <a href="/magic-books" class="text-vi-red hover:underline">magic books</a> devoted to taking magic with <a href="/playing-cards" class="text-vi-red hover:underline">playing cards</a> and moving them to a stage. But here you will find magic created for a larger audience.
            </p>
          </div>
          <div>
            <h2 class="mt-0 text-xl font-bold text-gray-900">Stage Magic Articles</h2>
            <p class="leading-relaxed text-gray-700">We have written some articles on stage and parlor magic we hope you will find useful.</p>
            <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
              <li><a href="/stage-and-parlor-magic/articles/the-difference-between-stage-and-parlor-magic/" class="text-vi-red hover:underline">What Is The Difference Between Stage And Parlor Magic?</a></li>
              <li><a href="/stage-and-parlor-magic/articles/best-selling-parlor-magic-tricks/" class="text-vi-red hover:underline">The Best-Selling Parlor Magic Tricks</a></li>
              <li><a href="/stage-and-parlor-magic/articles/parlor-magic-nyc/" class="text-vi-red hover:underline">Parlor Magic in NYC</a></li>
              <li><a href="/stage-and-parlor-magic/articles/parlour-tricks-the-book/" class="text-vi-red hover:underline">Parlour Tricks: The Book</a></li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  'virtual-magic-shows': {
    directory: {
      groupId: 'performance-style',
      order: 6,
      label: 'Virtual & Zoom shows',
      blurb: 'Routines and theory for Zoom, hybrid, and online performance.',
    },
    showTopSellersSidebar: true,
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <h2 class="mt-0 text-2xl font-bold text-gray-900">The Future Of Virtual Magic?</h2>
        <p class="leading-relaxed text-gray-700">
          We ran the largest survey into the future of virtual or Zoom magic shows. You can find the highlights of this research in the links below.
        </p>
        <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
          <li><a href="/virtual-magic-shows/articles/thoughts-on-zoom-magic/" class="text-vi-red hover:underline">Thoughts on Zoom Magic</a></li>
          <li><a href="/virtual-magic-shows/articles/hybrid-events-the-future/" class="text-vi-red hover:underline">Are Hybrid Events the Future?</a></li>
          <li><a href="/virtual-magic-shows/articles/cost-of-virtual-events/" class="text-vi-red hover:underline">Cost of Virtual Events</a></li>
          <li><a href="/virtual-magic-shows/articles/virtual-magic-is-not-dead/" class="text-vi-red hover:underline">Virtual Magic is not Dead</a></li>
        </ul>
      </div>
    `,
  },
  amazekids: {
    directory: {
      groupId: 'performance-style',
      order: 7,
      label: 'AmazeKids',
      blurb:
        'Colorful, practical magic for kids’ entertainers and family shows.',
    },
    showTopSellersSidebar: true,
    topSellersHeading: 'Top Selling Kids Magic',
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <div>
            <h2 class="mt-0 text-xl font-bold text-gray-900">Magic for Kids Entertainers</h2>
            <p class="leading-relaxed text-gray-700">
              AmazeKids was created because it needed to be. You see, we believe performing magic for kids can be an art, and there was no place for children's magicians to come together, learn together, and improve their shows.
            </p>
            <h2 class="mt-6 text-xl font-bold text-gray-900">Our own range of magic</h2>
            <p class="leading-relaxed text-gray-700">
              Modern kids magicians need new ideas, and so Vanishing Inc. has collaborated with Danny Orleans to produce a series of colorful, fun, modern routines for the modern children's entertainer.
            </p>
          </div>
          <div>
            <h2 class="mt-0 text-xl font-bold text-gray-900">Meet Danny Orleans</h2>
            <p class="leading-relaxed text-gray-700">
              With a degree in elementary education, real-time experience as both a kindergarten and middle school teacher and over thirty years of experience performing professionally for children, Danny Orleans is one of world's foremost experts on how to entertain young people. Based out of Chicago, he has performed Family Shows at kids' parties, schools, museums and huge theaters throughout North America. Scholastic Publishing hired him to author over ten books which teach children how to perform magic.
            </p>
            <p class="mt-4 leading-relaxed text-gray-700">
              Danny is one of the most respected creators, thinkers, and authors on children's magic. He has released two DVD sets on the <a href="/close-up-magic-downloads/art-of-presenting-magic-to-children-download/" class="text-vi-red hover:underline">Art of Presenting Magic to Children</a> and Teenagers as well as the critically acclaimed book, <a href="/selling-your-show/kids-show-masterplan/" class="text-vi-red hover:underline">Kids Show Masterplan</a>. All of this makes him perfectly suited to develop new products and curate this website as well as help you choose tricks for your show.
            </p>
          </div>
        </div>
      </div>
    `,
  },
  'card-magic': {
    directory: {
      groupId: 'performance-style',
      order: 1,
      blurb: 'Card tricks and routines — from quick effects to full sets.',
    },
    sidebarModal: 'tenByTenDownloadsSignup',
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <h2 class="mt-0 text-2xl font-bold text-gray-900">Card Magic</h2>
        <p class="leading-relaxed text-gray-700">
          Card tricks are probably the VI team's favorite type of magic trick. They are so versatile. You can always have a deck of playing cards in your pocket and be ready to go. Since cards were invented, people have been doing card tricks with them. And we are no exception.
        </p>
        <p class="mt-4 leading-relaxed text-gray-700">
          We have put together some articles on card magic we think you will find useful:
        </p>
        <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
          <li><a href="/card-magic/articles/best-mathematical-card-tricks/" class="text-vi-red hover:underline">What Are The Best Mathematical Card Tricks</a></li>
          <li><a href="/card-magic/articles/four-ways-to-do-a-magic-card-trick/" class="text-vi-red hover:underline">Four Ways To Do A Magic Card Trick</a></li>
          <li><a href="/card-magic/articles/worlds-easiest-best-card-tricks/" class="text-vi-red hover:underline">World's Easiest Best Card Tricks</a></li>
          <li><a href="/card-magic/articles/best-card-magic-tricks-2021/" class="text-vi-red hover:underline">The Best Card Magic Tricks of 2021</a></li>
          <li><a href="/card-magic/articles/the-most-popular-card-magic-tricks-ever/" class="text-vi-red hover:underline">The Most Popular Card Magic Tricks Ever</a></li>
          <li><a href="/card-magic/articles/learn-intermediate-card-tricks/" class="text-vi-red hover:underline">Learn Intermediate Card Tricks</a></li>
          <li><a href="/card-magic/articles/seven-strongest-sleights-for-card-magic/" class="text-vi-red hover:underline">Seven Strongest Sleights for Card Magic</a></li>
          <li><a href="/card-magic/articles/glossary-of-terms-used-in-card-magic/" class="text-vi-red hover:underline">Glossary of Terms Used in Card Magic</a></li>
        </ul>
      </div>
    `,
  },
  mentalism: {
    directory: {
      groupId: 'performance-style',
      order: 2,
      blurb:
        'Mind reading, predictions, and mental magic for every skill level.',
    },
    sidebarModal: 'magicInMindSignup',
    showTopSellersSidebar: true,
    showFilter: true,
    filters: [
      'exclusive',
      'discounted',
      'apps',
      'books',
      'downloads',
      'ebooks',
      'tricks',
      'dvds',
    ],
    showProductTypeInFilter: true,
    productTypeInFilter: 'mentalism',
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <div class="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
          <div>
            <h2 class="mt-0 text-2xl font-bold text-gray-900">Learn Mentalism at Vanishing Inc.</h2>
            <p class="leading-relaxed text-gray-700">
              We have always been fascinated with Mentalism here at Vanishing Inc. We have been running <a href="/magic-conventions/the-event-2020/" class="text-vi-red hover:underline">The Event</a> alongside our magic convention <a href="/magic-conventions/the-session/" class="text-vi-red hover:underline">The Session</a> for years which is dedicated to all things associated with Mentalism and had some incredible performers grace our stage there like Weber, Max Maven, Luke Jermay, The Evasons and many more.
            </p>
            <p class="mt-4 leading-relaxed text-gray-700">
              We thought it would be useful to put together a list of resources for people interested in Mentalism. From those just wanting to get started, to people interested in the history.
            </p>
          </div>
          <div>
            <h2 class="mt-0 text-xl font-bold text-gray-900">Useful Mentalism Articles</h2>
            <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
              <li><a href="/mentalism/articles/what-is-mentalism/" class="text-vi-red hover:underline">What Is Mentalism?</a></li>
              <li><a href="/mentalism/articles/history-of-mentalism/" class="text-vi-red hover:underline">The History of Mentalism</a></li>
              <li><a href="/mentalism/articles/top-famous-mentalists/" class="text-vi-red hover:underline">The Top 13 Mentalists</a></li>
              <li><a href="/mentalism/articles/popular-mentalism-tricks/" class="text-vi-red hover:underline">Popular Mentalism Tricks</a></li>
              <li><a href="/mentalism/articles/essential-mentalism-books/" class="text-vi-red hover:underline">Essential Mentalism Books that Every Mentalist Should Read</a></li>
              <li><a href="/mentalism/articles/easy-mentalism-tricks/" class="text-vi-red hover:underline">Easy Mentalism Tricks You Can Do</a></li>
              <li><a href="/mentalism/articles/how-does-cold-reading-work/" class="text-vi-red hover:underline">How Does Cold Reading Work?</a></li>
              <li><a href="/mentalism/articles/is-the-mentalist-tv-show-real/" class="text-vi-red hover:underline">Is The TV Show The Mentalist Real?</a></li>
              <li><a href="/mentalism/articles/are-playing-cards-bad-to-use-in-mentalism/" class="text-vi-red hover:underline">Are Playing Cards Bad to Use in Mentalism?</a></li>
              <li><a href="/mentalism/articles/glossary-of-mentalism-terms/" class="text-vi-red hover:underline">A Glossary of Mentalism and Mind Reading Terms</a></li>
              <li><a href="/mentalism/articles/how-to-get-started-in-mentalism/" class="text-vi-red hover:underline">How to Get Started in Mentalism</a></li>
              <li><a href="/mentalism/articles/can-ai-read-our-minds/" class="text-vi-red hover:underline">Can AI Read Our Minds?</a></li>
            </ul>
          </div>
        </div>
      </div>
    `,
  },
  'close-up-magic': {
    directory: {
      groupId: 'performance-style',
      order: 3,
      label: 'Close-up magic',
      blurb:
        'The world’s most popular style — magic inches from your audience.',
    },
    seoText: `
      <div class="rounded bg-gray-100 p-6">
        <h2 class="mt-0 text-2xl font-bold text-gray-900">Close-Up Magic</h2>
        <p class="leading-relaxed text-gray-700">
          Close-up magic is the most popular type of magic in the world. It is easy to see why. You can understand why. Everyone can learn how to do it, you do not need any special space or equipment to do it and you can always have a deck of playing cards in your pocket ready to go at a moments notice.
        </p>
        <p class="mt-4 leading-relaxed text-gray-700">
          We have put together some useful articles on close-up magic we think you will enjoy reading.
        </p>
        <ul class="mt-4 list-disc space-y-1 pl-5 text-gray-700">
          <li><a href="/close-up-magic/articles/celebrities-that-are-also-magicians/" class="text-vi-red hover:underline">Celebrities That Are Also Magicians</a></li>
          <li><a href="/close-up-magic/articles/what-are-the-greatest-close-up-tricks-of-all-time/" class="text-vi-red hover:underline">What Are The Greatest Close Up Magic Tricks Of All Time?</a></li>
          <li><a href="/close-up-magic/articles/secret-truth-behind-penn-and-teller-fool-us/" class="text-vi-red hover:underline">The Secret Truth Behind Penn &amp; Teller: Fool Us</a></li>
          <li><a href="/close-up-magic/articles/how-to-do-close-up-magic/" class="text-vi-red hover:underline">How to do Close-Up Magic</a></li>
          <li><a href="/close-up-magic/articles/how-to-practise-close-up-magic/" class="text-vi-red hover:underline">How To Practise Close Up Magic</a></li>
          <li><a href="/close-up-magic/articles/our-favorite-close-up-magic-tricks/" class="text-vi-red hover:underline">Our Favorite Close-Up Magic Tricks</a></li>
          <li><a href="/close-up-magic/articles/getting-started-in-close-up-magic/" class="text-vi-red hover:underline">Getting Started in Close-Up Magic</a></li>
          <li><a href="/close-up-magic/articles/online-close-up-magic-university/" class="text-vi-red hover:underline">Online Close-Up Magic University</a></li>
          <li><a href="/close-up-magic/articles/what-is-close-up-magic/" class="text-vi-red hover:underline">What Is Close-Up Magic?</a></li>
        </ul>
      </div>
    `,
  },
  'magic-ebooks': {
    includeInCategoryRouting: false,
    sidebarModal: 'magicInMindSignup',
  },
  'magic-apps': {
    directory: {
      groupId: 'apps-brand-starter',
      order: 1,
      label: 'Magic apps',
      blurb: 'Phone and tablet apps for practice, performance, and utility.',
    },
    showTopSellersSidebar: true,
    showFilter: true,
    showProductTypeInFilter: false,
    showStockInFilter: false,
    productListStyle: 'standard',
    productTypeInFilter: 'apps',
    footerName: 'MagicApps',
  },
  'free-magic-downloads': {
    directory: {
      groupId: 'digital',
      order: 8,
      label: 'Free magic downloads',
      blurb:
        'Free teaching and samples — build your library with a free account.',
    },
    showFilter: false,
    showProductTypeInFilter: false,
    breadcrumbs: [
      { name: 'Magic downloads', href: '/magic-downloads/' },
      { name: 'Free magic downloads', href: '#' },
    ],
    footerName: 'free-magic-downloads',
    metaTitle: 'Free magic downloads',
    metaDescription:
      'Free magic downloads from Vanishing Inc.—video tutorials, ebooks, and lecture notes from top creators. Create a free account, add titles to your library, and stream or download instantly.',
    seoText: `
      <h2 id="free-magic-downloads-seo-heading" class="mt-0 text-2xl font-bold text-gray-900">About this collection</h2>
      <p class="leading-relaxed text-gray-700">
        We keep this category as a permanent home for <strong>free magic downloads</strong>—so you can sample our teaching, find new creators, and grow your library without a paywall.
        The list above changes over time; every title is free to add with a Vanishing Inc. account. How to claim titles, where files live, and what formats to expect are covered in the <strong>questions and answers</strong> section below.
      </p>
      <p class="mt-6 leading-relaxed text-gray-700">
        Ready for the full catalog? Visit our <a href="/magic-downloads/" class="text-vi-red hover:underline">magic downloads</a> shop, or browse by interest—<a href="/card-magic-downloads/" class="text-vi-red hover:underline">card magic downloads</a>, <a href="/coin-magic-downloads/" class="text-vi-red hover:underline">coin magic downloads</a>, and more.
      </p>
    `,
  },
  // Custom component categories (use brandVariant*.tsx files)
  'brand-store': {
    directory: {
      groupId: 'apps-brand-starter',
      order: 2,
      label: 'Vanishing Inc. brand store',
      blurb: 'Vanishing Inc. originals — props and media from our own studio.',
    },
    showFilter: false,
    productListStyle: 'grid',
    routing: {
      useCustomComponent: true,
    },
  },
  'coin-shells': {
    directory: {
      groupId: 'coins-money',
      order: 1,
      blurb: 'Expanded shells, caps, and sets for professional coin work.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'gimmicked-coin-effects': {
    directory: {
      groupId: 'coins-money',
      order: 2,
      label: 'Gimmicked coin effects',
      blurb: 'Precision gaffs and specialty coins for workers and collectors.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'himber-wallets': {
    directory: {
      groupId: 'wallets',
      order: 1,
      label: 'Himber wallets',
      blurb:
        'Switch, load, and steal with the classic Himber and modern variants.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'flipper-coins': {
    directory: {
      groupId: 'coins-money',
      order: 3,
      label: 'Flipper coins',
      blurb: 'Flipper and hinged coins for spellbinding transformations.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'double-headed-coins': {
    directory: {
      groupId: 'coins-money',
      order: 4,
      label: 'Double-headed coins',
      blurb: 'Gaffed coins for appearances, transpositions, and comedy bits.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'hopping-halves': {
    directory: {
      groupId: 'coins-money',
      order: 5,
      blurb:
        'Hopping halves, sets, and routines in the coin-hopping tradition.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'coin-sets': {
    directory: {
      groupId: 'coins-money',
      order: 6,
      blurb: 'Matched sets for matrix, 3-fly, and structured coin routines.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'magnetic-coins': {
    directory: {
      groupId: 'coins-money',
      order: 7,
      blurb:
        'Magnetic gaffs and accessories for animation and secret alignment.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'tango-ultimate-coin': {
    directory: {
      groupId: 'coins-money',
      order: 8,
      label: 'Tango Ultimate Coin',
      blurb: 'Tango-style precision coins and expanded shells for workers.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'cigarette-through-coin': {
    directory: {
      groupId: 'coins-money',
      order: 9,
      label: 'Cigarette through coin',
      blurb: 'Penetration plots with coins — visual, portable, memorable.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'copper-silver-coins': {
    directory: {
      groupId: 'coins-money',
      order: 10,
      label: 'Copper / silver coins',
      blurb: 'Copper/silver sets and routines — a cornerstone of coin magic.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'bent-coins': {
    directory: {
      groupId: 'coins-money',
      order: 11,
      label: 'Bent / stretched coins',
      blurb: 'Bent, stretched, and signature coin plots for close-up.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'hook-coins': {
    directory: {
      groupId: 'coins-money',
      order: 12,
      blurb: 'Hook-style gaffs for steals, loads, and surprising productions.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'steel-core-coins': {
    directory: {
      groupId: 'coins-money',
      order: 13,
      label: 'Steel core coins',
      blurb: 'Steel-core and magnetic-ready coins for specific techniques.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'chinese-coins': {
    directory: {
      groupId: 'coins-money',
      order: 14,
      label: 'Chinese coins',
      blurb: 'Classic Chinese coins and sets for spellbound coin routines.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'bite-coin': {
    directory: {
      groupId: 'coins-money',
      order: 15,
      blurb: 'Bite-out and restored coin effects — instant audience reactions.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'scotch-and-soda': {
    directory: {
      groupId: 'coins-money',
      order: 16,
      label: 'Scotch & Soda',
      blurb: 'Scotch & Soda sets and cousins — pocket miracles since forever.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'stacks-of-coins': {
    directory: {
      groupId: 'coins-money',
      order: 17,
      blurb: 'Stacked-coin plots, productions, and surprising final loads.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'ungimmicked-coins': {
    directory: {
      groupId: 'coins-money',
      order: 18,
      label: 'Ungimmicked coins',
      blurb: 'Regular coins and sets for sleight-of-hand purists.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'jumbo-coins': {
    directory: {
      groupId: 'coins-money',
      order: 19,
      blurb: 'Jumbo coins for stage, parlor, and big-moment close-up.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'pull-coins': {
    directory: {
      groupId: 'coins-money',
      order: 20,
      blurb: 'Pull devices and coin pulls for vanishes and appearances.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'coin-gimmicks': {
    directory: {
      groupId: 'coins-money',
      order: 21,
      label: 'Coin gimmicks',
      blurb: 'Browse every coin gaff, insert, and utility in one place.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'worlds-greatest-magic': {
    directory: {
      groupId: 'digital',
      order: 9,
      label: "World's Greatest Magic (L&L)",
      blurb:
        'L&L’s best performances and teaching — downloads and DVDs by theme.',
    },
    routing: {
      useCustomComponent: true,
    },
    metaTitle: "World's Greatest Magic (downloads & DVDs)",
    metaDescription:
      'L&L Publishing DVDs and downloads organized by subject—the best performances of specific plots and moves. Browse card, close-up, coin, and gambling titles.',
  },
  'flash-paper': {
    directory: {
      groupId: 'classic-props',
      order: 1,
      blurb: 'Flash paper, string, and accessories for fire and flash moments.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  thumbtips: {
    directory: {
      groupId: 'classic-props',
      order: 2,
      label: 'Thumb tips',
      blurb: 'Thumb tips, loads, and classic thumb-tip miracles.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'chop-cups': {
    directory: {
      groupId: 'classic-props',
      order: 3,
      label: 'Chop cups',
      blurb: 'Chop cups and loads — one-cup classics for workers.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'cups-and-balls': {
    directory: {
      groupId: 'classic-props',
      order: 4,
      label: 'Cups & Balls',
      blurb: 'Cups and balls sets and routines — the backbone of conjuring.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'invisible-thread': {
    directory: {
      groupId: 'classic-props',
      order: 5,
      label: '(ITR) Invisible thread',
      blurb: 'ITR, reels, and thread work for floating and animation.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'force-switch-bags': {
    directory: {
      groupId: 'classic-props',
      order: 6,
      label: 'Force & switch bags',
      blurb:
        'Forcing, switching, and bag props for clean, fair-looking choices.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  rings: {
    directory: {
      groupId: 'classic-props',
      order: 7,
      blurb: 'Linking rings and ring plots from intimate to stage scale.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'sponge-balls': {
    directory: {
      groupId: 'classic-props',
      order: 8,
      label: 'Sponge balls',
      blurb: 'Sponge balls, rabbits, and routines that play huge for all ages.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'nail-writers': {
    directory: {
      groupId: 'classic-props',
      order: 9,
      label: 'Nail writers',
      blurb: 'Swami, nail writers, and secret writing for mentalism and magic.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  silks: {
    directory: {
      groupId: 'classic-props',
      order: 10,
      blurb: 'Silks, dye tubes, and silk magic for color and production.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  rope: {
    directory: {
      groupId: 'classic-props',
      order: 11,
      blurb: 'Rope magic — cuts, restores, and parlor standbys.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'pk-magnetic-rings': {
    directory: {
      groupId: 'classic-props',
      order: 12,
      label: 'PK rings & magnetic rings',
      blurb: 'PK rings, magnetic rings, and animation accessories.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  dlites: {
    directory: {
      groupId: 'classic-props',
      order: 13,
      label: "D'Lite",
      blurb: 'D’Lite and light-from-fingertip effects for any lighting.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  topits: {
    directory: {
      groupId: 'classic-props',
      order: 14,
      blurb: 'Topits, steals, and holdout-friendly clothing solutions.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  holdouts: {
    directory: {
      groupId: 'classic-props',
      order: 15,
      blurb: 'Holdouts and devices for invisible loads and steals.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'mouth-coils': {
    directory: {
      groupId: 'classic-props',
      order: 16,
      label: 'Mouth coils',
      blurb: 'Mouth coils and paper productions — big moments from nowhere.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'folding-coins': {
    directory: {
      groupId: 'coins-money',
      order: 22,
      label: 'Folding coins',
      blurb: 'Folding and bite coins — pack small, play huge.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  // Wallet categories - will eventually have proper variant pages
  // TODO: Create variant pages at app/variants/US/category/[wallet-name]/page.tsx
  'card-to-wallet': {
    directory: {
      groupId: 'wallets',
      order: 6,
      label: 'Card to wallet',
      blurb: 'Signed card to wallet — the worker’s favorite kicker.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'coin-purses': {
    directory: {
      groupId: 'wallets',
      order: 7,
      label: 'Coin purses',
      blurb:
        'Coin purses, purses, and pocket utility for coins and small props.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'fire-wallets': {
    directory: {
      groupId: 'wallets',
      order: 2,
      label: 'Fire wallets',
      blurb: 'Fire wallets and flash openers for memorable closers.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'magic-wallets': {
    directory: {
      groupId: 'wallets',
      order: 3,
      label: 'Magic wallets',
      blurb:
        'Everyday-carry wallets with magic built in — peek, load, and switch.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'packet-trick-wallets': {
    directory: {
      groupId: 'wallets',
      order: 4,
      label: 'Packet trick wallets',
      blurb: 'Wallets designed around packet tricks and small card sets.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'peek-wallets': {
    directory: {
      groupId: 'wallets',
      order: 5,
      label: 'Peek wallets',
      blurb: 'Peek devices and wallets for mentalism and card work.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'easy-magic-tricks': {
    directory: {
      groupId: 'beginners',
      order: 1,
      label: 'Easy magic tricks',
      blurb:
        'Beginner-friendly tricks and curated paths to your first miracles.',
    },
    routing: {
      // Special handling: bypassMiddleware is true, but middleware checks for custom routes
      // Custom pages: /, /card-tricks, /mentalism, /close-up-magic, /all, /free
      // Filter views like /card-magic use default category system (treated as filtered view of easy-magic-tricks)
      bypassMiddleware: true,
    },
  },

  'childrens-magic': {
    directory: {
      groupId: 'performance-style',
      order: 8,
      label: "Children's magic",
      blurb: 'Magic for kids, family audiences, and children’s entertainers.',
    },
  },
  'coin-boxes': {
    directory: {
      groupId: 'coins-money',
      order: 23,
      label: 'Coin boxes',
      blurb: 'Okito boxes, Boston boxes, and other coin box routines.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'vanishing-inc-pro': {
    directory: {
      groupId: 'apps-brand-starter',
      order: 3,
      label: 'For Pros, By Pros',
      blurb: 'Professional-grade Vanishing Inc. material and releases.',
    },
  },
  'gambling-downloads': {
    directory: {
      groupId: 'digital',
      order: 11,
      label: 'Gambling downloads',
      blurb:
        'Downloadable lessons on gambling demonstrations and card table work.',
    },
    productTypeInFilter: 'downloads',
    showProductTypeInFilter: false,
  },
  'gimmicked-playing-cards': {
    directory: {
      groupId: 'playing-cards',
      order: 5,
      label: 'Gimmicked playing cards',
      blurb: 'Special decks and card gaffs for impossible-looking card magic.',
    },
  },
  'magic-history': {
    directory: {
      groupId: 'books-media',
      order: 4,
      label: 'Magic history',
      blurb: 'Books and media about the history, people, and stories of magic.',
    },
  },
  'llpub-ebooks': {
    directory: {
      groupId: 'digital',
      order: 12,
      label: 'L&L Publishing ebooks',
      blurb: 'Digital editions from the L&L Publishing magic library.',
    },
    productTypeInFilter: 'downloads',
    showProductTypeInFilter: false,
  },
  'live-lectures': {
    directory: {
      groupId: 'digital',
      order: 13,
      label: 'Live lectures',
      blurb: 'Recorded lectures and long-form teaching from working magicians.',
    },
    productTypeInFilter: 'downloads',
    showProductTypeInFilter: false,
  },
  'magic-accessories': {
    directory: {
      groupId: 'classic-props',
      order: 18,
      label: 'Magic accessories',
      blurb:
        'Refills, utility items, and small accessories for everyday performance.',
    },
  },
  'magic-art': {
    directory: {
      groupId: 'apps-brand-starter',
      order: 4,
      label: 'Magic art',
      blurb:
        'Prints, artwork, and design-led pieces for magicians and collectors.',
    },
    showFilter: false,
    productListStyle: 'grid',
    thumbnailStyle: 'video',
    introHtml: `
      <p>
        Every creative individual has a room or space dedicated to practicing their craft. These areas are so important for providing joy and inspiration. For magicians, that room has often been filled with <a href="/magic-books/" class="text-vi-red hover:underline">magic books</a>, <a href="/magic-tricks/" class="text-vi-red hover:underline">magic tricks</a> and other <a href="/props/" class="text-vi-red hover:underline">magic gimmicks</a>. But now, you can easily forge an environment that fosters creativity with one-of-a-kind pieces of magic art. These stunning works of fine art are custom-made in small batches by some of the most talented artists in the world. Quantities are incredibly limited - so act quickly.
      </p>
    `,
  },
  'magic-theory': {
    directory: {
      groupId: 'books-media',
      order: 5,
      label: 'Magic theory',
      blurb:
        'Books and media on performance, construction, and stronger magic.',
    },
  },
  'packet-tricks': {
    directory: {
      groupId: 'performance-style',
      order: 9,
      label: 'Packet tricks',
      blurb:
        'Small-packet card tricks that are direct, portable, and practical.',
    },
  },
  'palming-coins': {
    directory: {
      groupId: 'coins-money',
      order: 24,
      label: 'Palming coins',
      blurb:
        'Coins designed for palming practice, productions, and coin technique.',
    },
    routing: {
      useCustomComponent: true,
    },
  },
  'gimmicked-decks': {
    directory: {
      groupId: 'playing-cards',
      order: 6,
      label: 'Gimmicked decks',
      blurb: 'Trick decks and special packs for powerful card routines.',
    },
    showFilter: true,
    showProductTypeInFilter: false,
  },
  'rubiks-cube-magic': {
    directory: {
      groupId: 'classic-props',
      order: 19,
      label: "Rubik's Cube magic",
      blurb:
        'Visual cube magic, solves, predictions, and modern cube routines.',
    },
  },
  'easy-card-tricks': {
    redirectTo: '/easy-magic-tricks/card-tricks/',
    directory: {
      groupId: 'beginners',
      order: 2,
      label: 'Easy card tricks',
      href: '/easy-magic-tricks/card-tricks/',
      blurb:
        'Beginner-friendly card tricks and simple routines with playing cards.',
    },
  },
  'easy-close-up-magic': {
    redirectTo: '/easy-magic-tricks/close-up-magic/',
    directory: {
      groupId: 'beginners',
      order: 3,
      label: 'Easy close-up magic',
      href: '/easy-magic-tricks/close-up-magic/',
      blurb: 'Simple close-up tricks for learning practical magic quickly.',
    },
  },
  'easy-mentalism': {
    redirectTo: '/easy-magic-tricks/mentalism/',
    directory: {
      groupId: 'beginners',
      order: 4,
      label: 'Easy mentalism',
      href: '/easy-magic-tricks/mentalism/',
      blurb:
        'Accessible mind-reading and prediction effects for newer performers.',
    },
  },
  // Routed category slugs without custom display settings.
  'vanishing-inc-decks': {},
  'tabletop-magic': {},
  'amazing-magic': {},
  refills: {},
  'magic-events': {},
  'poker-chip-magic': {
    routing: {
      useCustomComponent: true,
    },
  },
  magic: {
    redirectTo: '/magic-tricks/',
  },
  'poker-chip-gimmicks': {
    redirectTo: '/poker-chip-magic/',
  },
  'standard-decks': {
    redirectTo: '/playing-cards/',
  },
  explanations: {},
  'storytelling-magic': {},
  'sucker-tricks': {},
  'audience-participation-tricks': {},
  'live-animal-tricks': {},
  'gags-for-childrens-entertainers': {},
  'holiday-magic': {},
  'birthday-party-magic': {},
  'close-up-for-kids': {},
  'packs-flat-plays-big': {},
  'its-not-magic': {},
  'magical-giveaways': {},
  'selling-your-show': {},
  'magic-for-preschoolers': {},
  'childrens-entertainer-books': {},
  'childrens-entertainers-dvds': {},
  'childrens-magic-ebooks': {},
  videos: {},
  balloons: {},
  'childrens-magic-accessories': {},
  'magic-for-teens': {},
  'educational-magic': {},
  'balloon-twisting': {},
  'free-childrens-magic-downloads': {},
  'kids-magic-downloads': {},
  'magic-downloads': {
    routing: {
      bypassMiddleware: true,
    },
  },
  'magic-for-beginners': {
    redirectTo: '/easy-magic-tricks/',
  },
  'books-like-magic': {
    redirectTo: '/wonders/',
  },
  'home-and-living': {
    redirectTo: '/wonders/',
  },
  'mindful-play': {
    redirectTo: '/wonders/',
  },
  productivity: {
    redirectTo: '/wonders/',
  },
  'puzzles-and-brainteasers': {
    redirectTo: '/wonders/',
  },
  'magic-clothing': {
    redirectTo: '/brand-store/',
  },
  'everyday-carry': {
    redirectTo: '/magic-accessories/',
  },
  ibooks: {
    redirectTo: '/magic-downloads/',
  },
  'live-lecture-dvds': {
    redirectTo: '/live-lectures/',
  },
  'masters-of-magic': {
    redirectTo: '/magic-downloads/',
  },
  'phoenix-decks': {
    redirectTo: '/playing-cards/',
  },
  'out-of-print-magic-books': {
    redirectTo: '/magic-books/',
  },
  showtime: {
    redirectTo: '/magic-downloads/',
  },
  'fine-art': {
    redirectTo: '/magic-art/',
  },
  masterclass: {
    directory: {
      groupId: 'digital',
      order: 10,
      label: 'Vanishing Inc. Masterclass',
      blurb: 'Monthly deep dives with top creators, streamed on demand.',
    },
    routing: {
      bypassMiddleware: true, // Has custom page at app/masterclass/page.tsx
    },
  },
} satisfies Record<string, CategoryConfig>

type ConfiguredCategorySlug = keyof typeof categoryConfigs & string

const categoryConfigEntries = Object.entries(categoryConfigs) as [
  ConfiguredCategorySlug,
  CategoryConfig,
][]

function isRoutedCategoryConfig(config: CategoryConfig): boolean {
  return config.includeInCategoryRouting !== false
}

export const ALL_CATEGORY_SLUGS = categoryConfigEntries
  .filter(([, config]) => isRoutedCategoryConfig(config))
  .map(([slug]) => slug)

export type CategorySlug = (typeof ALL_CATEGORY_SLUGS)[number]

export const LEGACY_CATEGORY_REDIRECTS = Object.fromEntries(
  categoryConfigEntries
    .filter(([, config]) => isRoutedCategoryConfig(config))
    .filter(([, config]) => Boolean(config.redirectTo))
    .map(([slug, config]) => [slug, config.redirectTo as string])
)

const CATEGORY_DIRECTORY_GROUP_ORDER: CategoryBrowseGroupId[] = [
  'playing-cards',
  'performance-style',
  'books-media',
  'digital',
  'coins-money',
  'wallets',
  'classic-props',
  'beginners',
  'apps-brand-starter',
]

const CATEGORY_DIRECTORY_GROUP_COPY: Record<
  CategoryBrowseGroupId,
  Pick<CategoryDirectoryGroup, 'title' | 'description'>
> = {
  'playing-cards': {
    title: 'Playing cards',
    description:
      'Decks, storage, cardistry, and pocket accessories for every card worker.',
  },
  'performance-style': {
    title: 'Performance styles',
    description:
      'Browse by how you perform, from close-up and cards to stage, kids, and online.',
  },
  'books-media': {
    title: 'Books & magazines',
    description:
      'Printed books, DVDs, and periodicals when you want to learn away from the screen.',
  },
  digital: {
    title: 'Magic downloads',
    description:
      'Video downloads, ebooks, free samples, and streaming lessons you can start tonight.',
  },
  'coins-money': {
    title: 'Coins & money magic',
    description:
      'Gaffed coins, shells, sets, and every utility for bill-and-coin workers.',
  },
  wallets: {
    title: 'Wallets & carriers',
    description:
      'Peek, switch, load, and card-to-wallet, plus purses built for coins and packets.',
  },
  'classic-props': {
    title: 'Classic props',
    description:
      'Cups, silks, thread, flash, sponges, and the pocket classics every worker knows.',
  },
  beginners: {
    title: 'Beginner magic',
    description:
      'Easy card tricks, close-up magic, and mentalism for newer magicians.',
  },
  'apps-brand-starter': {
    title: 'Apps & brand store',
    description: 'Magic apps and Vanishing Inc. originals from our own studio.',
  },
}

function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export const CATEGORY_DIRECTORY_GROUPS: CategoryDirectoryGroup[] =
  CATEGORY_DIRECTORY_GROUP_ORDER.map(groupId => {
    const groupCopy = CATEGORY_DIRECTORY_GROUP_COPY[groupId]
    const links = categoryConfigEntries
      .filter(
        (
          entry
        ): entry is [
          ConfiguredCategorySlug,
          CategoryConfig & { directory: CategoryDirectoryConfig },
        ] => {
          const [, config] = entry
          return config.directory?.groupId === groupId
        }
      )
      .sort(([, a], [, b]) => {
        const byOrder = a.directory.order - b.directory.order
        if (byOrder !== 0) return byOrder
        return (a.directory.label ?? '').localeCompare(b.directory.label ?? '')
      })
      .map(([slug, config]) => ({
        slug,
        href: config.directory.href ?? `/${slug}/`,
        label: config.directory.label ?? humanizeSlug(slug),
        blurb: config.directory.blurb,
      }))

    return {
      id: groupId,
      ...groupCopy,
      links,
    }
  })

export const getCategoryConfig = (slug: string): CategoryConfig => {
  return {
    ...DEFAULT_CATEGORY_CONFIG,
    ...(categoryConfigs[slug as ConfiguredCategorySlug] ?? {}),
  }
}

/** Default sort segment for category URLs; per-category override or global newest. */
export const getDefaultCategorySortSlug = (categorySlug: string): string => {
  return (
    getCategoryConfig(categorySlug).defaultSortSlug ??
    DEFAULT_CATEGORY_SORT_SLUG
  )
}

/**
 * Get all category slugs that use custom components
 * Used for generating route patterns in middleware
 */
export const getCustomComponentCategories = (): string[] => {
  return categoryConfigEntries
    .filter(([, config]) => config.routing?.useCustomComponent === true)
    .map(([slug]) => slug)
}
